import React from 'react';
import { FormattedMessage } from 'react-intl';
import { addNotification } from 'reapop';
import { replace } from 'react-router-redux';
import { xSelectSingle } from 'utils/xSelect';
import { listWM, readWM, list, read, create, save, custom } from 'utils/Api';
import * as types from 'constants/ActionTypes';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import includes from 'lodash/includes';
import map from 'lodash/map';
import flattenDeep from 'lodash/flattenDeep';
import { filter, get, toNumber } from 'lodash';

export function setTab(tab) {
  return {
    type: types.SET_APPRAISAL_TAB,
    tab,
  };
}

export function setManagerYear(year) {
  return {
    type: types.SET_MANAGER_YEAR,
    year,
  };
}

export function setEmployee(employee) {
  return {
    type: types.SET_EMPLOYEE,
    employee,
  };
}

function requestManagerAppraisal() {
  return {
    type: types.REQUEST_MANAGER_APPRAISAL,
  };
}

function requestManagerAppraisalSuccess(appraisals) {
  return {
    type: types.REQUEST_MANAGER_APPRAISAL_SUCCESS,
    appraisals,
  };
}

function requestManagerAppraisalFailure(error) {
  return {
    type: types.REQUEST_MANAGER_APPRAISAL_FAILURE,
    error,
  };
}

export function fetchManagerAppraisal() {
  return (dispatch, getState) => {
    dispatch(requestManagerAppraisal());
    const { loginReducer: { user: { EmployeeMasterID, token } } } = getState();
    const config = {
      entity: 'PMAppraisal',
      headers: { Authorization: `Bearer ${token}` },
      params: {
        $filter: `Entity/ManagerEmployeeMasterID eq ${EmployeeMasterID}`,
        $orderby: 'Entity/ManagerDueDate asc',
      },
    };
    return listWM(config)
      .then(({ data }) => dispatch(requestManagerAppraisalSuccess(data)))
      .catch(error => dispatch(requestManagerAppraisalFailure(error)));
  };
}

async function patch(id, entityName, data) {
  await custom({
    url: `/kjarni/api/v2/${entityName}(${id})`,
    method: 'PATCH',
    data,
  });
}

export function fetchManagerAppraisalIfNeeded() {
  return (dispatch, getState) =>
    isEmpty(getState().ManagerAppraisal.appraisals) &&
    dispatch(fetchManagerAppraisal());
}

export function requestAppraisalById(appraisalId) {
  return {
    type: types.REQUEST_APPRAISAL_BY_ID,
    appraisalId,
  };
}

export function requestAppraisalByIdSuccess(appraisal, answers) {
  return {
    type: types.REQUEST_APPRAISAL_BY_ID_SUCCESS,
    appraisal,
    answers,
  };
}

export function requestAppraisalByIdFailure(error, appraisalId) {
  return {
    type: types.REQUEST_APPRAISAL_BY_ID_FAILURE,
    error,
    appraisalId,
  };
}

export function setDetailIDs(detailIDs) {
  return {
    type: types.SET_DETAIL_IDS,
    detailIDs,
  };
}

/* Helper function to sort the QAElement Children */
export function sortChildren(obj) {
  if (obj.Children) {
    return { ...obj.Entity, ...obj, Children: orderBy(obj.Children.map(sortChildren), ['Order'], ['asc']) };
  }
  return { ...obj.Entity, ...obj };
}

export function setFinalAnswers(appraisalId, answers) {
  return {
    type: types.SET_FINAL_ANSWERS,
    appraisalId,
    answers,
  };
}

export function setManagerAnswers(appraisalId, answers) {
  return {
    type: types.SET_MANAGER_ANSWERS,
    appraisalId,
    answers,
  };
}

export function setEmployeeAnswers(appraisalId, answers) {
  return {
    type: types.SET_EMPLOYEE_ANSWERS,
    appraisalId,
    answers,
  };
}

/*
  When fetching an appraisal we need to check if it has answers and if not we need to initalize
  root QAAnswer entities for the Appraisal
*/
export function initAnswers(appraisal) {
  return (dispatch, getState) => {
    const {
      EmployeeDetailID,
      EmployeeDueDate,
      EmployeeQAAnswerID,
      FinalQAAnswerID,
      ManagerDueDate,
      ManagerQAAnswerID,
      ManagerStatus,
      PMAppraisalID,
      PMForm,
    } = appraisal;

    const { loginReducer: { user: { detailIDs, token } } } = getState();

    const isEmployee = includes(detailIDs, EmployeeDetailID);

    const answerConfig = {
      entity: 'QAAnswer',
      headers: { Authorization: `Bearer ${token}` },
      data: {
        QAElementID: PMForm.QAElement.QAElementID,
      },
    };
    if (isEmployee && !EmployeeQAAnswerID) {
      create(answerConfig)
        .then((rootAnswer) => {
          const rootAnswerID = rootAnswer.data.QAAnswerID;

          /* Check if we should update the AnswerID for the Employee or the Manager */
          /* const conf = {
            entity: 'PMAppraisal',
            headers: { Authorization: `Bearer ${token}` },
            data: {
              ...appraisal.Entity,
              EmployeeQAAnswerID: rootAnswerID,
            },
          }; */

          /* Save the Appraisal with updated AnswerIDs */
          const data = {
            PMAppraisalID,
            EmployeeQAAnswerID: rootAnswerID,
          };
          patch(PMAppraisalID, 'PMAppraisals', data)
            .then(() => {
              const groups = PMForm.QAElements.filter(e => e.QAElementType === 2);

              /* To initialize the answers correctly we need to create QAAnswers for
          all the groups in the QAElement tree. This is done here so that it is
          not required on submit or save of the form */
              const createGroups = groups.map(({
                QAElementID,
                Label,
                Order,
              }) => create({
                entity: 'QAAnswer',
                headers: { Authorization: `Bearer ${token}` },
                data: {
                  QAAnswerParentID: rootAnswerID,
                  RootAnswerID: rootAnswerID,
                  QAElementID,
                  QAAnswerValue: Label,
                  QAAnswerText: Label,
                  QAAnswerOrder: Order,
                },
              }));
              Promise.all(createGroups)
                .then((QAA) => {
                  dispatch(setEmployeeAnswers(PMAppraisalID, { RootAnswerID: rootAnswer.data.QAAnswerID, QAAnswers: QAA.map(x => ({ ...x.data })) }));
                  dispatch(requestAppraisalByIdSuccess({ ...appraisal, EmployeeQAAnswerID: rootAnswerID, Entity: { ...get(appraisal, 'Entity'), EmployeeQAAnswerID: rootAnswerID } }));
                }).catch(error => dispatch(requestAppraisalByIdFailure(error, PMAppraisalID)));
            }).catch(error => dispatch(requestAppraisalByIdFailure(error, PMAppraisalID)));
        }).catch(error => dispatch(requestAppraisalByIdFailure(error, PMAppraisalID)));

    } else if (isEmployee) {
      const RootAnswerID = EmployeeQAAnswerID;
      const allRoots = `${ManagerQAAnswerID}, ${EmployeeQAAnswerID}, ${FinalQAAnswerID}`;
      const isFinal = (ManagerStatus === 2) || (new Date(ManagerDueDate).getTime() < Date.now() && new Date(EmployeeDueDate).getTime() < Date.now());
      const conf = {
        entity: 'QAAnswer',
        headers: { Authorization: `Bearer ${token}` },
        data: xSelectSingle([{ field: 'RootAnswerID', value: `${isFinal ? allRoots : RootAnswerID}`, relation: 'eq' }]),
      };
      list(conf)
        .then((QAA) => {
          const QAAnswers = QAA.data;
          if (isFinal) {
            dispatch(setFinalAnswers(PMAppraisalID, { RootAnswerID: FinalQAAnswerID, QAAnswers: QAAnswers.filter(x => x.RootAnswerID === FinalQAAnswerID) }));
            dispatch(setManagerAnswers(PMAppraisalID, { RootAnswerID: ManagerQAAnswerID, QAAnswers: QAAnswers.filter(x => x.RootAnswerID === ManagerQAAnswerID) }));
            dispatch(setEmployeeAnswers(PMAppraisalID, { RootAnswerID: EmployeeQAAnswerID, QAAnswers: QAAnswers.filter(x => x.RootAnswerID === EmployeeQAAnswerID) }));
          } else if (isEmployee) {
            dispatch(setEmployeeAnswers(PMAppraisalID, { RootAnswerID, QAAnswers }));
          }
          dispatch(requestAppraisalByIdSuccess(appraisal));
        }).catch(error => dispatch(requestAppraisalByIdFailure(error, PMAppraisalID)));
    } else {
      /* Should never happen, only when trying to access appraisal tha is not owned
      This prevents any user that is not registered to an Appraisal from fetching
      it either unknowingly or on purpose. IDEA: Might still be good idea to add GUID */
      dispatch(requestAppraisalByIdSuccess(appraisal));
    }
  };
}

/*
  Fetches the QAInput model for each QAElement adding it to the
  QAElement passed and returning the updated element.
  TODO: Do not perform this request for elements that have
  no InputOptions or ScaleEntries
*/
export function fetchOptions(qae, token) {
  const { QAInputID } = qae;
  const config = {
    entity: 'QAInput',
    entityID: QAInputID,
    headers: { Authorization: `Bearer ${token}` },
  };

  return read(config)
    .then(QAInput => ({ ...qae, QAInput: QAInput.data }));
}

/* Helper function that goes through all the QAElement childrent present and
moves them to a single array. */
const flattenChildren = (obj) => {
  if (obj.Children) {
    return [obj, ...flattenDeep(obj.Children.map(flattenChildren))];
  }
  return obj;
};

export function fetchAppraisalById(entityID) {
  return (dispatch, getState) => {
    dispatch(requestAppraisalById(entityID));
    const { loginReducer: { user: { token, EmployeeMasterID } } } = getState();

    const detailConfig = {
      entity: 'EmployeeDetail',
      headers: { Authorization: `Bearer ${token}` },
      data: xSelectSingle([{ field: 'EmployeeMasterID', value: EmployeeMasterID, relation: 'eq' }]),
    };

    list(detailConfig)
      .then((detailIDs) => {
        dispatch(setDetailIDs(detailIDs.data));
        const config = {
          entity: 'PMAppraisal',
          entityID,
          headers: { Authorization: `Bearer ${token}` },
        };
        readWM(config)
          .then(({ data }) => {
            const appraisal = data;
            if (appraisal.ManagerEmployeeMasterID !== EmployeeMasterID && !includes(map(detailIDs.data, x => x.EmployeeDetailID), appraisal.EmployeeDetailID)) {
              return dispatch(requestAppraisalByIdFailure(<FormattedMessage id="appraisal.error.mine" />, entityID));
            } else if (!appraisal.PMFormID) {
              return dispatch(requestAppraisalByIdFailure(<FormattedMessage id="appraisal.error.form" />, entityID));
            }
            const configForm = {
              entity: 'PMForm',
              entityID: appraisal.PMFormID,
              headers: { Authorization: `Bearer ${token}` },
            };
            return read(configForm)
              .then((PMF) => {
                const PMForm = PMF.data;
                const configElement = {
                  entity: 'QAElement',
                  entityID: PMForm.QAElementID,
                  headers: { Authorization: `Bearer ${token}` },
                };
                readWM(configElement)
                  .then((QAElement) => {
                    const initQAElements = flattenChildren(sortChildren(QAElement.data));
                    const QAElements = initQAElements.map((e) => {
                      if (e.QAInput) {
                        return { ...e, ...e.Entity, QAInput: { ...e.QAInput.Entity, ...e.QAInput } };
                      }
                      return { ...e, ...e.Entity };
                    });
                    dispatch(initAnswers({
                      ...appraisal,
                      PMForm: {
                        ...PMForm,
                        QAElement: sortChildren(QAElement.data),
                        QAElements,
                      },
                    }));
                  }).catch(error => dispatch(requestAppraisalByIdFailure(error, entityID)));
              }).catch(error => dispatch(requestAppraisalByIdFailure(error, entityID)));
          }).catch(error => dispatch(requestAppraisalByIdFailure(error, entityID)));
      }).catch(error => dispatch(requestAppraisalByIdFailure(error, entityID)));
  };
}

export function fetchAppraisalByIdIfNeeded(id) {
  return (dispatch, getState) =>
    (isEmpty(getState().Appraisal.appraisalsById[id]) || getState().Appraisal.expiredById[id]) &&
  dispatch(fetchAppraisalById(id));
}

export function requestAppraisal() {
  return {
    type: types.REQUEST_APPRAISAL,
  };
}

export function requestAppraisalSuccess(appraisals, openAppraisals) {
  return {
    type: types.REQUEST_APPRAISAL_SUCCESS,
    appraisals,
    openAppraisals,
  };
}

export function requestAppraisalFailure(error) {
  return {
    type: types.REQUEST_APPRAISAL_FAILURE,
    error,
  };
}

export function fetchAppraisal() {
  return (dispatch, getState) => {
    dispatch(requestAppraisal());
    const { loginReducer: { user: { EmployeeMasterID, token } } } = getState();
    const detailConfig = {
      entity: 'EmployeeDetail',
      headers: { Authorization: `Bearer ${token}` },
      data: xSelectSingle([{ field: 'EmployeeMasterID', value: EmployeeMasterID, relation: 'eq' }]),
    };

    return list(detailConfig)
      .then((ids) => {
        const config = {
          entity: 'PMAppraisal',
          headers: { Authorization: `Bearer ${token}` },
          params: {
            $filter: `${ids.data.map(({ EmployeeDetailID }) => `Entity/EmployeeDetailID eq ${EmployeeDetailID}`).join` or `}`,
            $orderby: 'Entity/EmployeeDueDate asc',
          },
        };
        listWM(config)
          .then(({ data: appraisals }) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const openAppraisals = filter(appraisals, a => (new Date(a.EmployeeDueDate).getTime() >= today.getTime()) && a.EmployeeStatus < 2);
            dispatch(requestAppraisalSuccess(appraisals, openAppraisals));
          })
          .catch(error => dispatch(requestAppraisalFailure(error)));
      })
      .catch(error => dispatch(requestAppraisalFailure(error)));
  };
}

export function resetAppraisalLoading() {
  return {
    type: types.RESET_APPRAISAL_LOADING,
  };
}

export function fetchAppraisalIfNeeded() {
  return (dispatch, getState) =>
    isEmpty(getState().Appraisal.appraisals) &&
  dispatch(fetchAppraisal());
}

export function setEmployeeYear(year) {
  return {
    type: types.SET_EMPLOYEE_YEAR,
    year,
  };
}

export function savingAppraisal() {
  return {
    type: types.SAVING_APPRAISAL,
  };
}

export function savingAppraisalSuccess(id) {
  return {
    type: types.SAVING_APPRAISAL_SUCCESS,
    id,
  };
}

export function savingAppraisalFailure(error) {
  return {
    type: types.SAVING_APPRAISAL_FAILURE,
    error,
  };
}

export function saveAppraisal(answers, id, cb) {
  return (dispatch, getState) => {
    dispatch(savingAppraisal());
    const {
      Appraisal: {
        appraisalsById,
      },
      loginReducer: {
        user: {
          token,
        },
      },
    } = getState();
    const appraisal = appraisalsById[id];
    Promise.all(answers.map((data) => {
      if (data.QAAnswerID) {
        return save({ entity: 'QAAnswer', data, headers: { Authorization: `Bearer ${token}` } });
      }
      return create({ entity: 'QAAnswer', data, headers: { Authorization: `Bearer ${token}` } });
    }))
      .then(() => {
        if (cb) {
          dispatch(savingAppraisalSuccess(id));
          dispatch(initAnswers(appraisal));
          cb();
        } else {
          const data = {
            PMAppraisalID: toNumber(id),
            EmployeeStatus: 'Started',
          };
          patch(id, 'PMAppraisals', data)
            .then(() => {
              dispatch(fetchAppraisal());
              dispatch(fetchManagerAppraisal());
              dispatch(savingAppraisalSuccess(id));
              dispatch(initAnswers(appraisal));
              dispatch(addNotification({
                title: <FormattedMessage id="appraisal.notification.saved" />,
                message: <FormattedMessage id="appraisal.notification.saved.message" />,
                status: 'success',
                dismissible: true,
                dismissAfter: 4000,
              }));
            }).catch(error => dispatch(savingAppraisalFailure(error)));
        }
      }).catch(error => dispatch(savingAppraisalFailure(error)));
  };
}

export function submittingAppraisal() {
  return {
    type: types.SUBMIT_APPRAISAL,
  };
}

export function submittingAppraisalSuccess(id) {
  return {
    type: types.SUBMIT_APPRAISAL_SUCCESS,
    id,
  };
}

export function submittingAppraisalFailure(error) {
  return {
    type: types.SUBMIT_APPRAISAL_FAILURE,
    error,
  };
}

export function submitAppraisal(answers, appraisalId) {
  return (dispatch) => {
    dispatch(submittingAppraisal());
    dispatch(saveAppraisal(answers, appraisalId, () => {
      /* should never happen on emp web */
      // const isManager = ManagerEmployeeMasterID === EmployeeMasterID;
      // const isFinal = (ManagerStatus === 2 && EmployeeStatus === 2) || (ManagerStatus === 2 && (new Date(EmployeeDueDate).getTime() < Date.now()));
      const data = {
        PMAppraisalID: toNumber(appraisalId),
        EmployeeStatus: 'Finished',
      };
      patch(appraisalId, 'PMAppraisals', data)
        .then(() => {
          dispatch(addNotification({
            title: <FormattedMessage id="appraisal.notification.submitted" />,
            message: <FormattedMessage id="appraisal.notification.submitted.message" />,
            status: 'success',
            dismissible: true,
            dismissAfter: 4000,
          }));
          dispatch(fetchAppraisal());
          dispatch(fetchManagerAppraisal());
          dispatch(submittingAppraisalSuccess(appraisalId));
          dispatch(replace('/appraisals'));
        }).catch(error => dispatch(savingAppraisalFailure(error)));
    }));
  };
}



// WEBPACK FOOTER //
// ./src/actions/Appraisal.js